import { useEffect, useState } from "react";
import { Space, Modal, Form, Input, Button, Select, message, Spin } from "antd";
import axios from "axios";
import { useFetchData } from "../context/FetchDataContext";
import Helper from "../utils/helper";
import { ENDPOINTS } from "../constants/api";
import moment from "moment";

const { Option } = Select;

const CreateForm = ({ isVisible, onClose, updateTableData }) => {
  const { globalUpdateTableData, setTriggerRefetch } = useFetchData();
  const [form] = Form.useForm();
  const [relationshipManagers, setRelationshipManagers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modelOptions, setModelOptions] = useState([]);

  useEffect(() => {
    if (isVisible) {
      const storedRMs = localStorage.getItem("relationshipManagers");
      if (storedRMs) {
        setRelationshipManagers(JSON.parse(storedRMs));
      }
    }
  }, [isVisible]);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await axios.get(ENDPOINTS.GET_ALL_MODELS);
        const models = response.data.items || [];
        const options = models.map((m) => ({
          label: m.name,
          value: m.modelLine,
        }));
        console.log("Fetched models:", options);
        setModelOptions(options);
      } catch (error) {
        console.error("Error fetching models:", error);
      }
    };

    fetchModels();
  }, []);

  const addTemporaryCustomer = (values) => {
    const tempCustomer = {
      ID: "temp_" + Date.now(),
      LeadID: "Pending",
      FirstName: values.first_name,
      LastName: values.last_name,
      PreferredModel: values.preferred_model,
      RMName:
        relationshipManagers.find((rm) => rm.emp_id === values.rm_name)
          ?.first_name || "Pending",
      CreatedAt: moment().format("YYYY-MM-DD HH:mm:ss"),
      PhoneNumber: values.phone_number,
    };

    console.log("Adding temporary customer in CreateForm:", tempCustomer);
    if (typeof updateTableData === "function") {
      // Use the prop if it's provided (for direct usage from ListPage)
      updateTableData(tempCustomer);
    } else if (typeof globalUpdateTableData === "function") {
      // Use the global function if the prop is not provided (for usage from Navbar)
      globalUpdateTableData(tempCustomer);
    } else {
      console.warn("No valid update function available");
    }
  };

  const handleOk = async (values) => {
    setLoading(true);

    // Add temporary customer to the list

    addTemporaryCustomer(values);

    // Show "Request sent" message
    message.loading("Request sent. Returning to list page...", 2).then(() => {
      // Close the modal and reset form after the message duration
      handleCancel();
    });

    // Perform API calls in the background
    performApiCalls(values);
  };

  const performApiCalls = async (values) => {
    try {
      const authToken = Helper.getTokenFromLocalStorage();
      const authorizationHeader = authToken ? `${authToken}` : "";

      // First API call to get customers

      // Change phone number to string
      const phoneNumber = values.phone_number; // {{ edit_1 }}

      // Retrieve dealer code from local storage
      const dealerCode = localStorage.getItem("dealer_code"); // {{ edit_1 }}

      // First API call
      let leadId;

      const customerTrackerPayload = {
        lead_id: leadId,
        phone_number: phoneNumber, // {{ edit_2 }}
        first_name: values.first_name,
        last_name: values.last_name,
        email_id: values.email_id || "",
        address: "",
        preferred_model: values.preferred_model,
        type_of_purchase: "",
        comments: "",
        emp_id: values.rm_name,
        dealer_code: dealerCode,
      };

      await axios.post(ENDPOINTS.CREATE_CUSTOMER, customerTrackerPayload, {
        headers: { Authorization: authorizationHeader },
      });

      // Trigger a refetch of the customer list
      setTriggerRefetch(true);
      message.success("Customer added successfully!");
    } catch (error) {
      console.error("An error occurred:", error);
      message.error("Failed to add customer. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
    form.resetFields();
  };

  return (
    <Modal
      title="Add Customer"
      open={isVisible}
      onCancel={handleCancel}
      footer={null}
    >
      <Spin spinning={loading} tip="Adding customer...">
        <Form form={form} onFinish={handleOk} layout="vertical">
          <Form.Item label="Phone Number" required>
            <Space.Compact>
              <Select
                defaultValue="+91"
                style={{ width: "25%", minWidth: "75px", marginRight: "2px" }}
              >
                <Option value="+91">+91</Option>
              </Select>
              <Form.Item
                name="phone_number"
                noStyle
                rules={[
                  { required: true, message: "Please input the phone number" },
                  {
                    pattern: /^[0-9]{10}$/,
                    message: "Please enter a valid 10-digit phone number",
                  },
                ]}
                validateTrigger={["onBlur", "onSubmit"]}
              >
                <Input
                  style={{ width: "74%" }}
                  placeholder="Enter phone number"
                  maxLength={10}
                  onKeyPress={(event) => {
                    if (!/[0-9]/.test(event.key)) {
                      event.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </Space.Compact>
          </Form.Item>
          <Form.Item label="Customer Name" required>
            <Space.Compact style={{ display: "flex", flexWrap: "wrap" }}>
              <Form.Item
                name="salutation"
                noStyle
                rules={[
                  { required: true, message: "Please select a salutation" },
                ]}
              >
                <Select
                  placeholder="Salutation"
                  style={{ width: "25%", marginRight: "2px" }}
                >
                  <Option value="Mr">Mr</Option>
                  <Option value="Mrs">Mrs</Option>
                  <Option value="Miss">Ms</Option>
                </Select>
              </Form.Item>
              <Form.Item
                name="first_name"
                noStyle
                rules={[
                  { required: true, message: "Please input the first name" },
                ]}
              >
                <Input
                  style={{ flex: 1, marginRight: "2px" }}
                  placeholder="First Name"
                />
              </Form.Item>
              <Form.Item
                name="last_name"
                noStyle
                rules={[
                  { required: true, message: "Please input the last name" },
                ]}
              >
                <Input style={{ flex: 1 }} placeholder="Last Name" />
              </Form.Item>
            </Space.Compact>
          </Form.Item>
          <Form.Item
            name="email_id"
            label="Email"
            rules={[{ type: "email", message: "Please input a valid email" }]}
          >
            <Input placeholder="Enter email" />
          </Form.Item>
          <Form.Item
            name="preferred_model"
            label="Preferred Model"
            rules={[
              { required: true, message: "Please select a preferred model" },
            ]}
          >
            <Select placeholder="Preferred Model">
              {modelOptions.map((model) => (
                <Option key={model.value} value={model.value}>
                  {model.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="rm_name"
            label="SM Name"
            rules={[{ required: true, message: "Please select an SM name" }]}
          >
            <Select placeholder="Select SM">
              {relationshipManagers.map((rm) => (
                <Option key={rm.emp_id} value={rm.emp_id}>
                  {`${rm.first_name} ${rm.last_name}`}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Button type="primary" htmlType="submit" disabled={loading}>
            {loading ? "Adding..." : "Add Customer"}
          </Button>
        </Form>
      </Spin>
    </Modal>
  );
};

export default CreateForm;
